import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleIcon } from "@/features/auth/components/google-icon";
import {
	type AccountFormValues,
	accountSchema,
} from "../../schemas/onboarding-schema";
import type { AccountViolations } from "../../utils/violations";

interface AccountStepProps {
	defaultValues?: Partial<AccountFormValues>;
	error?: string;
	violations?: AccountViolations;
	loading?: boolean;
	emailReadOnly?: boolean;
	hideGoogle?: boolean;
	onSubmit: (values: AccountFormValues) => void;
	onGoogleSignIn?: () => void;
}

export function AccountStep({
	defaultValues,
	error,
	violations,
	loading = false,
	emailReadOnly = false,
	hideGoogle = false,
	onSubmit,
	onGoogleSignIn,
}: AccountStepProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<AccountFormValues>({
		resolver: zodResolver(accountSchema),
		defaultValues: {
			name: "",
			email: "",
			password: "",
			confirmPassword: "",
			...defaultValues,
		},
	});
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	return (
		<form noValidate className="account-step" onSubmit={handleSubmit(onSubmit)}>
			<div className="account-step__heading">
				<h1 className="account-step__title">Crie sua conta</h1>
				<p className="account-step__subtitle">
					Comece a organizar suas obras e canteiros em poucos minutos.
				</p>
			</div>

			{!!error && (
				<p role="alert" className="form-error-alert">
					{error}
				</p>
			)}

			<div className="form-group">
				<Label htmlFor="name">Nome completo</Label>
				<Input
					id="name"
					autoComplete="name"
					placeholder="Como devemos te chamar?"
					aria-invalid={!!errors.name || !!violations?.name}
					{...register("name")}
				/>
				{(errors.name || violations?.name) && (
					<p className="form-error">
						{errors.name?.message ?? violations?.name}
					</p>
				)}
			</div>

			<div className="form-group">
				<Label htmlFor="email">E-mail</Label>
				<Input
					id="email"
					type="email"
					autoComplete="email"
					placeholder="voce@empresa.com.br"
					readOnly={emailReadOnly}
					aria-invalid={!!errors.email || !!violations?.email}
					{...register("email")}
				/>
				{(errors.email || violations?.email) && (
					<p className="form-error">
						{errors.email?.message ?? violations?.email}
					</p>
				)}
			</div>

			<div className="form-group">
				<Label htmlFor="password">Senha</Label>
				<div className="password-field">
					<Input
						id="password"
						type={showPassword ? "text" : "password"}
						autoComplete="new-password"
						placeholder="Mínimo de 8 caracteres"
						aria-invalid={!!errors.password || !!violations?.password}
						className="password-field__input"
						{...register("password")}
					/>
					<button
						type="button"
						onClick={() => setShowPassword((value) => !value)}
						aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
						className="password-field__reveal"
					>
						{showPassword ? <EyeOff /> : <Eye />}
					</button>
				</div>
				{(errors.password || violations?.password) && (
					<p className="form-error">
						{errors.password?.message ?? violations?.password}
					</p>
				)}
			</div>

			<div className="form-group">
				<Label htmlFor="confirmPassword">Confirmar senha</Label>
				<div className="password-field">
					<Input
						id="confirmPassword"
						type={showConfirmPassword ? "text" : "password"}
						autoComplete="new-password"
						placeholder="Repita a senha"
						aria-invalid={!!errors.confirmPassword}
						className="password-field__input"
						{...register("confirmPassword")}
					/>
					<button
						type="button"
						onClick={() => setShowConfirmPassword((value) => !value)}
						aria-label={
							showConfirmPassword
								? "Ocultar confirmação da senha"
								: "Mostrar confirmação da senha"
						}
						className="password-field__reveal"
					>
						{showConfirmPassword ? <EyeOff /> : <Eye />}
					</button>
				</div>
				{errors.confirmPassword && (
					<p className="form-error">{errors.confirmPassword.message}</p>
				)}
			</div>

			<Button type="submit" className="account-step__submit" disabled={loading}>
				{loading && <Loader2 className="auth-spinner" />}
				Criar conta
			</Button>

			{!hideGoogle && (
				<>
					<div className="divider-with-text">
						<span>ou continue com</span>
					</div>

					<Button
						type="button"
						variant="outline"
						className="account-step__social"
						disabled={loading}
						onClick={onGoogleSignIn}
					>
						<GoogleIcon />
						Continuar com Google
					</Button>
				</>
			)}

			<p className="account-step__footer">
				Já tem uma conta?{" "}
				<Link to="/login" className="account-step__login-link">
					Entrar
				</Link>
			</p>
		</form>
	);
}
